import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useInstitution } from './InstitutionContext';
import { useAuth } from './AuthContext';
import {
  fetchInstitutionStats,
  fetchInstitutionTrendStats,
  fetchInstitutions,
} from '../services/api';
import IconTextChip from './IconTextChip';
import StatCard from '../dashboard/components/StatCard';

const defaultData = Array(30).fill(0);

const InstitutionStatsCards = () => {
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState(null);
  const [institutionName, setInstitutionName] = useState('');

  useEffect(() => {
    if (!selectedInstitution) return;
    let isMounted = true;
    fetchInstitutionStats(selectedInstitution)
      .then((data) => {
        if (isMounted) setStats(data);
      })
      .catch((err) => {
        console.error('Failed to load institution stats:', err);
      });
    fetchInstitutionTrendStats(selectedInstitution)
      .then((data) => {
        if (isMounted) setTrend(data);
      })
      .catch((err) => {
        console.error('Failed to load institution trend stats:', err);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedInstitution]);

  useEffect(() => {
    const nameFromUser = user?.userprofile?.institution?.display_name;
    if (nameFromUser) {
      setInstitutionName(nameFromUser);
      return;
    }
    if (!selectedInstitution) {
      setInstitutionName('');
      return;
    }
    const loadName = async () => {
      try {
        const list = await fetchInstitutions();
        const inst = list.find(
          (i) => String(i.id) === String(selectedInstitution),
        );
        if (inst) setInstitutionName(inst.display_name);
      } catch (err) {
        console.error('Failed to load institution:', err);
      }
    };
    loadName();
  }, [user, selectedInstitution]);

  if (!stats) return null;

  const diff = (arr) =>
    Array.isArray(arr) && arr.length >= 2 ? arr[arr.length - 1] - arr[arr.length - 2] : null;

  const cards = [
    {
      title: 'Users',
      value: String(stats.users),
      data: trend?.users || defaultData,
      change: diff(trend?.users),
    },
    {
      title: 'Professors',
      value: String(stats.professors),
      data: trend?.professors || defaultData,
      change: diff(trend?.professors),
    },
    {
      title: 'Reviews',
      value: String(stats.reviews),
      data: trend?.reviews || defaultData,
      change: diff(trend?.reviews),
    },
    {
      title: 'Threads',
      value: String(stats.threads),
      data: trend?.threads || defaultData,
      change: diff(trend?.threads),
    },
  ];

  return (
    <Container
      id="institution-stats"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 4, sm: 8 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          textAlign: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <Typography component="h2" variant="h4" sx={{ color: 'text.primary' }}>
            Institution Statistics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          {institutionName && (
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {institutionName}
            </Typography>
          )}
          <IconTextChip
            icon={<EditOutlinedIcon />}
            label="Change"
            onClick={() => navigate('/settings#institution')}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          width: '100%',
          justifyContent: 'center',
        }}
      >
        {cards.map((card) => (
          <Box
            key={card.title}
            sx={{ flex: '1 1 200px', minWidth: 200 }}
          >
            <StatCard
              title={card.title}
              value={card.value}
              trend={
                card.change > 0 ? 'up' : card.change < 0 ? 'down' : 'neutral'
              }
              change={card.change}
              data={card.data}
            />
          </Box>
        ))}
      </Box>
    </Container>
  );
};

InstitutionStatsCards.propTypes = {
  // no props
};

export default InstitutionStatsCards;
